import Phaser from 'phaser';
import { PLAYER_SCALE, getEntityScale } from '../constants/scale';
import { fieldGrid } from '../constants/field';
import type { Weapon } from './weapons/Weapon';
import { Inventory } from './weapons/Inventory';

// ХИТБОКС — игровой инвариант, прежняя геометрия torso+head. Спрайт деда
// визуально шире (вытянутый ствол), но в хитбокс не входит. Эти константы НЕ
// определяют видимый размер — только collision/clamps.
const TORSO_WIDTH = 60 * PLAYER_SCALE;
const TORSO_HEIGHT = 80 * PLAYER_SCALE;

// Рига игрока — 2 наложенных спрайта (порядок снизу вверх): legs (спрайтшит ходьбы),
// top (per-weapon rig_top_<id>, статичный). TOP full-frame 1279×935.
const HERO_SPRITE_SOURCE_H = 935;
// Подобрано на глаз — visible высота ~ предыдущему плейсхолдеру. Крути это
// одно число чтобы изменить размер деда целиком.
const HERO_SPRITE_SCALE = 0.09;
// TOP origin (0.250, 0.984) — точка ног, ставится В позицию игрока (px, py).
const HERO_TOP_ORIGIN_X = 0.25;
const HERO_TOP_ORIGIN_Y = 0.984;
// Ноги — спрайтшит 4-кадровой ходьбы (см. BootScene 'player_legs_walk').
// Origin (0.48, 0.02) — «верх-центр таза» в кадре legs (Y=0.02 = почти самый
// верх кадра). По задумке эта точка приземляется на ТАЛИЮ деда = на origin TOP'а
// в системе контейнера, т.е. в (0, 0) + калибровочные оффсеты.
const HERO_LEGS_ANIM_KEY = 'player_legs_walk';
const HERO_LEGS_ORIGIN_X = 0.48;
const HERO_LEGS_ORIGIN_Y = 0.02;

// === КАЛИБРОВКА LEG-СПРАЙТА (3 крутилки в одном месте) ===
// Применяются ПОВЕРХ HERO_SPRITE_SCALE: scale legs = HERO_SPRITE_SCALE × LEG_SCALE_REL.
// Зачем: leg-ассет нарисован крупнее по нативному кадру (1443×1512) чем top (1279×935),
// и художник сделал ноги визуально ~1.7× больше нужного. 0.59 ≈ 1/1.7 — стартовое,
// калибруй по виду: ширина ремня ног ≈ низ куртки top. Привязка к HERO_SPRITE_SCALE
// и через container.scaleX (=entityScale) держит пропорции на любом разрешении.
const HERO_LEGS_SCALE_REL = 0.58;
// Смещение origin legs относительно (0, 0) контейнера в долях displayW/H legs.
// Y отрицательный = таз ВВЕРХ под куртку (перекрытие маскирует стык). X зеркалится
// по facingSign — при flip направления смещение тоже зеркалится автоматически.
const HERO_LEGS_HIP_OFFSET_X = -0.76;
const HERO_LEGS_HIP_OFFSET_Y = -0.61;
// timeScale анимации ходьбы привязан к фактической скорости через MOVE_SPEED.
// Floor 0.3 — при еле движении кадры всё-таки идут (иначе ноги фризятся при
// слабых отклонениях стика). Cap 1.5 — против слишком резкого галопа.
const HERO_LEGS_TIMESCALE_MIN = 0.3;
const HERO_LEGS_TIMESCALE_MAX = 1.5;
// Дуло относительно origin TOP'а (0.25, 0.984 — точка НОГ). Y=-0.74 ставит
// muzzle на уровень руки/ствола (≈ 24% от верха текстуры). Одинаково для всех
// оружий — рука деда нарисована на одной высоте. X варьируется по оружию (см.
// HERO_MUZZLE_X_BY_WEAPON ниже): кончик ствола у револьвера в одной точке,
// у снайперки — гораздо дальше. При flipX знак X зеркалится автоматически
// — см. getMuzzlePosition.
const HERO_MUZZLE_OFFSET_Y_RATIO = -0.74;

// Per-weapon X-offset от origin TOP'а (0.25 от левого края) до кончика ствола
// в долях displayWidth. Значения сняты с риг-картинок (1279×935).
//   ratio = (gun_tip_x_in_texture / texture_width) - 0.25
// Короткие пистолеты (револьвер, узи) — низкий ratio; длинные винтовки —
// высокий. Неуказанные → HERO_MUZZLE_X_DEFAULT.
// Тяжёлая группа (shotgun/assault_rifle/machinegun/laser) дополнительно
// сдвинута к телу на −0.20 — у них длинные стволы и вспышка визуально
// уезжала за кончик, отдельно подправили только их.
const HERO_MUZZLE_X_BY_WEAPON: Record<string, number> = {
  revolver: 0.20,
  dual_revolver: 0.20,
  uzi: 0.13,
  grenade_launcher: 0.17,
  shotgun: 0.20,
  assault_rifle: 0.22,
  machinegun: 0.22,
  laser: 0.22,
  sniper: 0.46,
};
const HERO_MUZZLE_X_DEFAULT = 0.30;
// Малое разнесение по Y для двух стволов DualRevolver — сохраняем для
// визуального шума парных выстрелов. × PLAYER_SCALE для baked-консистентности.
const BARREL_OFFSET_Y_TOP = -8 * PLAYER_SCALE;
const BARREL_OFFSET_Y_BOTTOM = 8 * PLAYER_SCALE;

// Красная вспышка при ударе и tint-сброс на белый — теперь через setTint(red)
// вместо setFillStyle на торсе/голове (Sprite не имеет fillStyle).
const FLASH_TINT_RED = 0xff4444;

// === ПРОГРАММНАЯ АНИМАЦИЯ ГЕРОЯ ===
// Дыхание в idle — лёгкая пульсация вертикального масштаба спрайта.
// История: 0.02 (исходно) → 0.008 (W13 — на 2% дед заметно «качался» в покое,
// особенно после уменьшения наклона/отдачи; 0.8% — едва уловимое подвсхлипывание).
const HERO_BREATH = 0.008; // +0.8% к scaleY на пике вдоха
const HERO_BREATH_MS = 1600; // полный цикл вдох-выдох
// Наклон при движении — корпус кренится «вперёд» по ходу.
// История: 4 (исходно) → 1.5 (W13 — на 4° дед визуально «переламывался»
// в поясе, особенно после уменьшения отдачи; 1.5° — намёк, не наклон).
const HERO_LEAN_ANGLE = 1.5; // градусов
const HERO_LEAN_MS = 150; // плавный переход к/от leanAngle
// Отдача при выстреле — кик назад против facing + лёгкий поворот.
// История: 10/5 (исходно) → 4/2 (W13 калибровка по виду — «чуть-чуть» вместо
// заметного дёрга, отдача читается как акцент, не как кинетика).
const HERO_RECOIL_PX = 2;
const HERO_RECOIL_ANGLE = 1; // градусов
const HERO_RECOIL_OUT_MS = 60; // кик: 0 → -PX/ANGLE
const HERO_RECOIL_BACK_MS = 120; // возврат: -PX/ANGLE → 0
// Плавный fade дыхания при движении/возврате к idle.
const HERO_BREATH_FADE_MS = 200;

// Y-смещение муззла от центра игрока. Используется в GameScene.getReachableSpawnYMax
// для clamp'а Y-зоны спавна врагов (чтобы пуля их доставала). Формула:
// SOURCE_H × HERO_SPRITE_SCALE × Y_RATIO × entityScale = -0.32 × displayHeight.
export function getPlayerMuzzleYOffsetFromCenter(scene: Phaser.Scene): number {
  return (
    HERO_SPRITE_SOURCE_H *
    HERO_SPRITE_SCALE *
    HERO_MUZZLE_OFFSET_Y_RATIO *
    getEntityScale(scene)
  );
}

// Половина высоты ХИТБОКСА игрока (НЕ спрайта) — для getSafeMaxY clamp'а,
// torso bottom = центр + halfHeight × entityScale. Спрайт визуально может
// выходить ниже хитбокса (ноги/сапоги), это норма.
export const PLAYER_TORSO_HALF_HEIGHT = TORSO_HEIGHT / 2;
const MOVE_SPEED = 400;
const ACCELERATION = 2400;
const DECELERATION = 2000;
// Балансовый множитель чувствительности джойстика. Без него вектор стика подаётся
// как ±1, что даёт максимум MOVE_SPEED = 400 px/s и резкое движение — сложно
// позиционировать игрока по Y для прицельной стрельбы. 0.45 = ~2.2× медленнее
// при касании пальцем; клавиатуру не трогает (она идёт отдельной веткой выше).
// Игрок всё ещё доходит до краёв поля, просто медленнее.
// История: 0.5 (Волна 1) → 0.45 (докрутка Волны 1, −10%) для ещё более плавного прицела.
const JOYSTICK_INPUT_SCALE = 0.45;
const BOAR_HIT_KNOCKBACK_PX = 80;
const BOAR_HIT_INVULN_MS = 1500;
const BOAR_HIT_FLASH_MS = 100;
const INVULN_BLINK_PERIOD_MS = 200;
const BOAR_MAX_HITS = 3;

function approachValue(current: number, target: number, maxDelta: number): number {
  const diff = target - current;
  if (Math.abs(diff) <= maxDelta) return target;
  return current + Math.sign(diff) * maxDelta;
}

export class Player extends Phaser.GameObjects.Container {
  // Рига: порядок (legs → top) задан порядком добавления в container —
  // последний добавленный рисуется сверху. legs — спрайтшит ходьбы.
  private readonly legs: Phaser.GameObjects.Sprite;
  private readonly top: Phaser.GameObjects.Sprite;
  // Текущий weaponId на TOP-текстуре; чтобы не дёргать setTexture каждый кадр.
  private currentTopWeaponId?: string;
  private cursors!: Phaser.Types.Input.Keyboard.CursorKeys;
  private keyW!: Phaser.Input.Keyboard.Key;
  private keyA!: Phaser.Input.Keyboard.Key;
  private keyS!: Phaser.Input.Keyboard.Key;
  private keyD!: Phaser.Input.Keyboard.Key;
  public joystickInput: { dx: number; dy: number } = { dx: 0, dy: 0 };
  public inventory: Inventory = new Inventory();

  public boarHits = BOAR_MAX_HITS;
  public readonly maxBoarHits = BOAR_MAX_HITS;
  public isInvulnerable = false;
  public isBeingDragged = false;
  public isShootingBlocked = false;
  // Множитель max-скорости от внешних ЗОННЫХ эффектов (1.0 = норма, 0.5 = в слизи).
  // Выставляется ИЗВНЕ (GameScene каждый кадр), Player сам не сбрасывает.
  public speedMultiplier = 1.0;
  // Временное замедление от попадания снаряда (плевок верблюда). Свой таймер, не зона.
  // Композиция со speedMultiplier — МУЛЬТИПЛИКАТИВНАЯ: target = base × slime × temporary.
  // Стак двух дебаффов ощутимо хуже одного.
  public temporarySlowFactor = 1.0;
  private invulnerabilityElapsedMs = 0;
  private shootingBlockElapsedMs = 0;
  private shootingBlockDurationMs = 0;
  private temporarySlowElapsedMs = 0;
  private temporarySlowDurationMs = 0;

  get currentWeapon(): Weapon {
    return this.inventory.getCurrentWeapon();
  }

  get hitboxWidth(): number {
    return TORSO_WIDTH * this.scaleX;
  }

  get hitboxHeight(): number {
    return TORSO_HEIGHT * this.scaleY;
  }

  private velocityX = 0;
  private velocityY = 0;

  // Параметры программной анимации спрайта. Применяются ПОВЕРХ базовой
  // синхронизации в applyAnimationToSprite() каждый кадр — нельзя твинить
  // sprite.x/angle напрямую, иначе синхронизация их затрёт. recoilOffsetX и
  // recoilAngle — это scalar-смещения, направление зеркалится через facingSign.
  private recoilOffsetX = 0;
  private recoilAngle = 0;
  private leanAngle = 0;
  private breathScale = 1.0;
  private breathTween?: Phaser.Tweens.Tween;
  private leanTween?: Phaser.Tweens.Tween;
  private recoilTween?: Phaser.Tweens.Tween;
  // Кэш текущей цели lean'а — чтобы не перезапускать tween каждый кадр на
  // одинаковом inputX. Хранится в градусах (0, +ANGLE, -ANGLE).
  private leanTargetAngle = 0;
  // false = в режиме fade-to-1.0 (движение); true = бесконечный yoyo дыхания.
  private isBreathing = false;

  constructor(scene: Phaser.Scene, x: number, y: number) {
    super(scene, x, y);
    // Рига — 2 части. TOP лицом ВПРАВО в текстуре → setFlipX(true) показывает
    // деда влево (стреляем влево к врагам). LEGS — ассет lеgs_walk_left_*,
    // лицом ВЛЕВО, без флипа. Оба после применённых флипов смотрят влево.
    this.legs = scene.add
      .sprite(0, 0, HERO_LEGS_ANIM_KEY, 0)
      .setOrigin(HERO_LEGS_ORIGIN_X, HERO_LEGS_ORIGIN_Y)
      .setScale(HERO_SPRITE_SCALE * HERO_LEGS_SCALE_REL);
    // TOP — стартует с револьвером (default starting weapon). Текстура
    // переключается в syncTopWeaponSprite() при смене оружия.
    this.top = scene.add
      .sprite(0, 0, 'rig_top_revolver')
      .setOrigin(HERO_TOP_ORIGIN_X, HERO_TOP_ORIGIN_Y)
      .setScale(HERO_SPRITE_SCALE)
      .setFlipX(true);
    this.currentTopWeaponId = 'revolver';
    this.add([this.legs, this.top]);
    scene.add.existing(this);

    // Адаптивный масштаб от высоты экрана. HERO_SPRITE_SCALE задаёт базовый
    // размер деда; entityScale применяется поверх через container.setScale —
    // на 720 = 1.0 (идентично), на 360 = 0.55 (вдвое меньше визуально).
    this.setScale(getEntityScale(scene));

    this.cursors = scene.input.keyboard!.createCursorKeys();
    this.keyW = scene.input.keyboard!.addKey('W');
    this.keyA = scene.input.keyboard!.addKey('A');
    this.keyS = scene.input.keyboard!.addKey('S');
    this.keyD = scene.input.keyboard!.addKey('D');

    // Старт idle-дыхания (yoyo bounce). При движении глушится в updateBreathState.
    this.startBreathingTween();
  }

  // Границы движения — чисто grid, без пиксельных коррекций. Хитбокс
  // центрируется на player.x/y; края могут торчать в буферные клетки.
  // X: cells 2..37 из 40 → буфер по 2 колонке слева/справа.
  // Y: cells 4..18 из 20 → буфер 4 ряда сверху (под HUD: защита/wave-баннер/
  // boss HP-bar), 1 ряд снизу.
  private getMinX(): number {
    return fieldGrid(this.scene).cell(2, 0).x;
  }

  private getMaxX(): number {
    return fieldGrid(this.scene).cell(37, 0).right;
  }

  private getMinY(): number {
    return fieldGrid(this.scene).cell(0, 4).y;
  }

  private getMaxY(): number {
    return fieldGrid(this.scene).cell(0, 18).bottom;
  }

  override update(delta: number): void {
    if (this.isBeingDragged) {
      this.velocityX = 0;
      this.velocityY = 0;
      this.tickInvulnerability(delta);
      this.updateLegsAnimation(false);
      this.syncTopWeaponSprite();
      this.applyAnimationToSprite();
      return;
    }

    let inputX = 0;
    let inputY = 0;

    if (this.cursors.up.isDown || this.keyW.isDown) inputY -= 1;
    if (this.cursors.down.isDown || this.keyS.isDown) inputY += 1;
    if (this.cursors.left.isDown || this.keyA.isDown) inputX -= 1;
    if (this.cursors.right.isDown || this.keyD.isDown) inputX += 1;

    if (inputX === 0 && inputY === 0) {
      inputX = this.joystickInput.dx * JOYSTICK_INPUT_SCALE;
      inputY = this.joystickInput.dy * JOYSTICK_INPUT_SCALE;
    }

    const inputLen = Math.sqrt(inputX * inputX + inputY * inputY);
    if (inputLen > 1) {
      inputX /= inputLen;
      inputY /= inputLen;
    }

    const dt = delta / 1000;

    const targetVelocityX = inputX * MOVE_SPEED * this.speedMultiplier * this.temporarySlowFactor;
    const targetVelocityY = inputY * MOVE_SPEED * this.speedMultiplier * this.temporarySlowFactor;

    this.velocityX = approachValue(
      this.velocityX,
      targetVelocityX,
      (inputX !== 0 ? ACCELERATION : DECELERATION) * dt,
    );
    this.velocityY = approachValue(
      this.velocityY,
      targetVelocityY,
      (inputY !== 0 ? ACCELERATION : DECELERATION) * dt,
    );

    const minX = this.getMinX();
    const maxX = this.getMaxX();
    const minY = this.getMinY();
    const maxY = this.getMaxY();

    this.x = Phaser.Math.Clamp(this.x + this.velocityX * dt, minX, maxX);
    this.y = Phaser.Math.Clamp(this.y + this.velocityY * dt, minY, maxY);

    if (this.x === minX || this.x === maxX) this.velocityX = 0;
    if (this.y === minY || this.y === maxY) this.velocityY = 0;

    this.tickInvulnerability(delta);
    this.tickShootingBlock(delta);
    this.tickTemporarySlow(delta);

    // Анимация поверх базовой синхронизации. moving — есть ли горизонтальный
    // или вертикальный input. Lean — только по горизонтали (по ходу).
    const moving = inputX !== 0 || inputY !== 0;
    this.updateBreathState(moving);
    this.updateLean(inputX);
    this.updateLegsAnimation(moving);
    this.syncTopWeaponSprite();
    this.applyAnimationToSprite();
  }

  applyTemporarySlow(factor: number, durationMs: number): void {
    this.temporarySlowFactor = factor;
    this.temporarySlowElapsedMs = 0;
    this.temporarySlowDurationMs = durationMs;
  }

  private tickTemporarySlow(deltaMs: number): void {
    if (this.temporarySlowFactor >= 1.0) return;
    this.temporarySlowElapsedMs += deltaMs;
    if (this.temporarySlowElapsedMs >= this.temporarySlowDurationMs) {
      this.temporarySlowFactor = 1.0;
      this.temporarySlowElapsedMs = 0;
    }
  }

  blockShootingFor(durationMs: number): void {
    this.isShootingBlocked = true;
    this.shootingBlockElapsedMs = 0;
    this.shootingBlockDurationMs = durationMs;
  }

  private tickShootingBlock(deltaMs: number): void {
    if (!this.isShootingBlocked) return;
    this.shootingBlockElapsedMs += deltaMs;
    if (this.shootingBlockElapsedMs >= this.shootingBlockDurationMs) {
      this.isShootingBlocked = false;
    }
  }

  applyBoarHit(): boolean {
    if (this.isInvulnerable) return false;
    if (this.boarHits <= 0) return false;

    this.boarHits -= 1;
    this.startInvulnerability();
    this.applyKnockback();
    this.flashDamage();

    if (this.boarHits <= 0) {
      this.die();
      return true;
    }
    return false;
  }

  // === АНИМАЦИЯ: запуск, смена режимов, применение на спрайт ===

  private startBreathingTween(): void {
    this.breathTween?.stop();
    this.isBreathing = true;
    this.breathTween = this.scene.tweens.add({
      targets: this,
      breathScale: 1.0 + HERO_BREATH,
      duration: HERO_BREATH_MS / 2,
      ease: 'Sine.easeInOut',
      yoyo: true,
      repeat: -1,
    });
  }

  private updateBreathState(moving: boolean): void {
    if (moving && this.isBreathing) {
      // Глушим дыхание плавным сходом к 1.0.
      this.isBreathing = false;
      this.breathTween?.stop();
      this.breathTween = this.scene.tweens.add({
        targets: this,
        breathScale: 1.0,
        duration: HERO_BREATH_FADE_MS,
        ease: 'Sine.easeOut',
      });
    } else if (!moving && !this.isBreathing) {
      // Игрок встал — снова запускаем yoyo от текущего breathScale.
      this.startBreathingTween();
    }
  }

  private updateLean(inputX: number): void {
    // Цель lean'а — ±ANGLE по знаку inputX (направление движения). При покое — 0.
    const target = inputX === 0 ? 0 : Math.sign(inputX) * HERO_LEAN_ANGLE;
    if (target === this.leanTargetAngle) return;
    this.leanTargetAngle = target;
    this.leanTween?.stop();
    this.leanTween = this.scene.tweens.add({
      targets: this,
      leanAngle: target,
      duration: HERO_LEAN_MS,
      ease: 'Sine.easeOut',
    });
  }

  // Отдача — публичный метод, вызывается из GameScene.shoot/handleBeamWeapon.
  // Один активный recoil-твин: новый вызов kill'ит предыдущий и стартует новый
  // от ТЕКУЩЕГО значения recoilOffsetX/Angle (без reset к 0, чтобы при
  // непрерывном огне sprite не дёргался к нулю на каждой пуле).
  playRecoil(): void {
    this.recoilTween?.stop();
    // Phase 1: кик 0 → -PX, -ANGLE (или от текущего, если recoil уже шёл).
    this.recoilTween = this.scene.tweens.add({
      targets: this,
      recoilOffsetX: -HERO_RECOIL_PX,
      recoilAngle: -HERO_RECOIL_ANGLE,
      duration: HERO_RECOIL_OUT_MS,
      ease: 'Quad.easeOut',
      onComplete: () => {
        // Phase 2: плавный возврат к 0.
        this.recoilTween = this.scene.tweens.add({
          targets: this,
          recoilOffsetX: 0,
          recoilAngle: 0,
          duration: HERO_RECOIL_BACK_MS,
          ease: 'Quad.easeIn',
        });
      },
    });
  }

  // Синхронизирует текстуру TOP'а с текущим оружием (rig_top_<id>). Вызывается
  // каждый кадр — никакого внешнего хука из GameScene не требуется. Цена 1
  // сравнения строк; setTexture только при смене id.
  private syncTopWeaponSprite(): void {
    const id = this.currentWeapon.config.id;
    if (this.currentTopWeaponId === id) return;
    const key = `rig_top_${id}`;
    if (!this.scene.textures.exists(key)) return;
    this.top.setTexture(key);
    this.currentTopWeaponId = id;
  }

  // Управление walk-анимацией. moving → играем зацикленно, timeScale пропорционален
  // фактической скорости (=скорость / MOVE_SPEED, clamp в MIN..MAX) — ноги идут
  // быстрее когда игрок несётся, медленнее при стиковой полу-скорости. На покое
  // (включая dragged) — стоп на кадре контакта (frame 0).
  private updateLegsAnimation(moving: boolean): void {
    if (moving) {
      const currentKey = this.legs.anims.currentAnim?.key;
      if (currentKey !== HERO_LEGS_ANIM_KEY || !this.legs.anims.isPlaying) {
        this.legs.play({ key: HERO_LEGS_ANIM_KEY });
      }
      const speed = Math.hypot(this.velocityX, this.velocityY);
      this.legs.anims.timeScale = Phaser.Math.Clamp(
        speed / MOVE_SPEED,
        HERO_LEGS_TIMESCALE_MIN,
        HERO_LEGS_TIMESCALE_MAX,
      );
      return;
    }
    if (this.legs.anims.isPlaying) {
      this.legs.anims.stop();
      this.legs.setFrame(0);
    }
  }

  // Накладывает текущее анимационное состояние на 2 части риги. Вызывается
  // из update() КАЖДЫЙ КАДР после расчёта позиции тела. Архитектура:
  // - TOP: lokal (recoilOffsetX×sign, 0), angle = lean + recoil×sign, scale с breath.
  // - LEGS: позиция в hipLocal (бедро в системе TOP'а × disp). Ноги наклон/отдачу
  //   корпуса НЕ наследуют — стык перекрывается запасом таза в ассете.
  // breathScale модулирует ТОЛЬКО scaleY TOP'а; ноги не пульсируют.
  private applyAnimationToSprite(): void {
    const facingSign = this.top.flipX ? -1 : 1;

    // TOP: позиция + angle + breath. Origin (0.25, 0.984) на (0, 0) контейнера.
    this.top.x = this.recoilOffsetX * facingSign;
    this.top.y = 0;
    this.top.angle = this.leanAngle + this.recoilAngle * facingSign;
    this.top.setScale(HERO_SPRITE_SCALE, HERO_SPRITE_SCALE * this.breathScale);

    // LEGS: точка крепления (origin 0.48, 0.02 — верх-центр таза legs)
    // приземляется на (0, 0) контейнера = талию деда. Калибровочные оффсеты
    // (HERO_LEGS_HIP_OFFSET_*) задвигают таз ВВЕРХ под куртку, чтобы запас
    // вверху ассета перекрылся низом TOP'а без зазора. Scale = HERO_SPRITE_SCALE
    // × LEG_SCALE_REL — компенсация того что нативный кадр legs крупнее top.
    const offsetX = HERO_LEGS_HIP_OFFSET_X * this.legs.displayWidth * facingSign;
    const offsetY = HERO_LEGS_HIP_OFFSET_Y * this.legs.displayHeight;
    this.legs.setPosition(offsetX, offsetY);
    this.legs.setScale(HERO_SPRITE_SCALE * HERO_LEGS_SCALE_REL);
  }

  restoreBoarHits(): void {
    this.boarHits = this.maxBoarHits;
    this.isInvulnerable = false;
    this.invulnerabilityElapsedMs = 0;
    this.isBeingDragged = false;
    this.isShootingBlocked = false;
    this.shootingBlockElapsedMs = 0;
    this.temporarySlowFactor = 1.0;
    this.temporarySlowElapsedMs = 0;
    this.setAlpha(1.0);
    this.top.clearTint();
    this.legs.clearTint();
  }

  die(): void {
    this.scene.events.emit('player:died');
  }

  startBeingDragged(): void {
    this.isBeingDragged = true;
    this.flashDamage();
  }

  private startInvulnerability(): void {
    this.isInvulnerable = true;
    this.invulnerabilityElapsedMs = 0;
  }

  // Отлёт игрока вправо на BOAR_HIT_KNOCKBACK_PX, clamp на правую границу.
  private applyKnockback(): void {
    this.x = Math.min(this.x + BOAR_HIT_KNOCKBACK_PX, this.getMaxX());
  }

  // Красная вспышка на 100мс. setTint применяется к обеим частям риги —
  // торс с оружием и ноги краснеют синхронно. Sprite не имеет fillStyle.
  private flashDamage(): void {
    this.top.setTint(FLASH_TINT_RED);
    this.legs.setTint(FLASH_TINT_RED);
    this.scene.time.delayedCall(BOAR_HIT_FLASH_MS, () => {
      this.top.clearTint();
      this.legs.clearTint();
    });
  }

  private tickInvulnerability(deltaMs: number): void {
    if (!this.isInvulnerable) return;
    this.invulnerabilityElapsedMs += deltaMs;

    const phase = Math.floor(this.invulnerabilityElapsedMs / INVULN_BLINK_PERIOD_MS) % 2;
    this.setAlpha(phase === 0 ? 0.4 : 1.0);

    if (this.invulnerabilityElapsedMs >= BOAR_HIT_INVULN_MS) {
      this.isInvulnerable = false;
      this.setAlpha(1.0);
    }
  }

  // Положение дула в WORLD координатах. Дуло относительно origin TOP'а
  // (точка ног, 0.25/0.984): X из per-weapon таблицы (короткие пистолеты ~0.4,
  // длинные винтовки ~0.7), Y фиксированный -0.74 (рука одна для всех стволов).
  // При flipX (стрельба влево) знак X зеркалится. worldDisplay* учитывает и
  // sprite-scale (HERO_SPRITE_SCALE), и container scaleX/Y (entityScale).
  getMuzzlePosition(): { x: number; y: number } {
    const dispW = this.top.displayWidth * this.scaleX;
    const dispH = this.top.displayHeight * this.scaleY;
    const sign = this.top.flipX ? -1 : 1;
    const weaponId = this.currentWeapon.config.id;
    const xRatio = HERO_MUZZLE_X_BY_WEAPON[weaponId] ?? HERO_MUZZLE_X_DEFAULT;
    return {
      x: this.x + sign * xRatio * dispW,
      y: this.y + HERO_MUZZLE_OFFSET_Y_RATIO * dispH,
    };
  }

  // Отдача — игрок смещается вправо (X+) на forcePx, в пределах границ.
  // Если уже у границы — recoil поглощается границей и не накапливается дальше.
  applyRecoil(forcePx: number): void {
    const minX = this.getMinX();
    const maxX = this.getMaxX();
    if (forcePx > 0 && this.x + forcePx > maxX) {
      this.x = maxX;
      return;
    }
    if (forcePx < 0 && this.x + forcePx < minX) {
      this.x = minX;
      return;
    }
    this.x += forcePx;
  }

  // Для парных оружий: barrelIndex 0 — верхний ствол, 1 — нижний. Базовая
  // muzzle-точка одна (центр револьвера), верх/низ — небольшое Y-разнесение
  // ±BARREL_OFFSET для визуального шума парных выстрелов.
  getMuzzlePositionForBarrel(barrelIndex: number): { x: number; y: number } {
    const base = this.getMuzzlePosition();
    const dy = barrelIndex === 0 ? BARREL_OFFSET_Y_TOP : BARREL_OFFSET_Y_BOTTOM;
    return { x: base.x, y: base.y + dy * this.scaleY };
  }

  prewarm(): void {
    this.x += 1;
    this.y += 1;
    this.top.getWorldTransformMatrix();
    this.x -= 1;
    this.y -= 1;
    this.top.getWorldTransformMatrix();
  }
}
